import { ReactComponent as IconEarth } from "../../assets/icons/globe.svg";
import React from "react";
import { useIntl } from "react-intl";
import { style } from "typestyle";
import { ReactComponent as IconLocation } from "../../assets/icons/location.svg";
import Heading from "../../components/Heading";
import {
  LazyImageRenderer,
  Badge,
  BadgeGroup,
  GenericTextSkeleton,
  BadgeSkeleton,
  DescriptionSkeleton,
  LazyImageRendererSkeleton,
} from "@hooplab/ui-components";
import prettyUrl from "../../helpers/prettyUrl";
import colors from "../../styles/colors";
import { mb4 } from "../../styles/spacing";
import { Organization } from "../../types";
import Description from "../Event/Description";
import EventList, { EventListSkeleton } from "./EventList";
import * as styles from "./styles";
import { pageView } from "../../analytics";
import MediaQuery, { useMediaQuery } from "react-responsive";
import mediaQueries, { upFrom } from "../../styles/mediaQueries";

type OrganizationEventListProps = {
  organization: Organization;
};

const OrganizationEventList: React.FC<OrganizationEventListProps> = ({ organization }) => {
  React.useEffect(() => pageView(), []);

  const intl = useIntl();
  const fromTablet = useMediaQuery(upFrom(mediaQueries.tablet));

  return (
    <>
      <div className={styles.top}>
        <div className={styles.coverAndInfo} style={{ color: colors.grey[900] }}>
          <div className={styles.info}>
            <Heading
              size={fromTablet ? "xl4" : "xl3"}
              className={"mb-4 text-ellipsis overflow-hidden"}
            >
              {organization.name}
            </Heading>

            {organization.location && (
              <div className={style(mb4)}>
                <BadgeGroup>
                  <Badge
                    icon={
                      <IconLocation
                        width={14}
                        height={14}
                        color={colors.grey[400]}
                        className={style({ minWidth: 14 })}
                      />
                    }
                  >
                    {organization.location}
                  </Badge>
                </BadgeGroup>
              </div>
            )}
            <div style={{ marginBottom: "1.25rem" }}>
              <Description
                text={organization.description}
                maxHeight={200}
                fontSize={"1rem"}
                hideHeader={true}
                setPracticalInfoModalOpen={() => {}}
              />
            </div>
            <div className={styles.links}>
              {organization.website && (
                <div className="block whitespace-nowrap text-ellipsis overflow-hidden">
                  <IconEarth className={styles.linkIcon} height={16} width={16} />
                  <a
                    href={"https://" + prettyUrl(organization.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Home"
                  >
                    {prettyUrl(organization.website)}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className={styles.cover} data-nosnippet>
            <div className={styles.image}>
              <MediaQuery {...upFrom(mediaQueries.desktopSmall)}>
                <LazyImageRenderer
                  imgixImage={organization.image}
                  croppedImages={organization.images}
                  style={{ width: "100%", aspectRatio: "4/3" }}
                  base={{ width: 400, ar: "FourByThree" }}
                  queries={[]}
                  env={window.ENVIRONMENT.__IMGIX_ENV__}
                  alt={intl.formatMessage(
                    { id: "components.LazyImageRenderer.imageFor", defaultMessage: "Image for" },
                    { image: organization.name },
                  )}
                />
              </MediaQuery>
            </div>
          </div>
        </div>
      </div>

      <EventList organizationName={organization.name} />
    </>
  );
};

export const OrganizationEventListSkeleton = () => {
  const fromTablet = useMediaQuery(upFrom(mediaQueries.tablet));
  return (
    <>
      <div className={styles.top}>
        <div className={styles.coverAndInfo} style={{ color: colors.grey[900] }}>
          <div className={styles.info}>
            <div className={style(mb4)}>
              <GenericTextSkeleton fontSize={fromTablet ? "3rem" : "2.25rem"} />
            </div>
            <div className={style(mb4)}>
              <BadgeGroup>
                <BadgeSkeleton />
              </BadgeGroup>
            </div>
            <div style={{ marginBottom: "1.25rem" }}>
              <DescriptionSkeleton maxHeight={200} />
            </div>
            <div className={styles.links}>
              <GenericTextSkeleton />
            </div>
          </div>

          <div className={styles.cover}>
            <div className={styles.image}>
              <MediaQuery {...upFrom(mediaQueries.desktopSmall)}>
                <LazyImageRendererSkeleton
                  style={{ aspectRatio: "4/3", width: 400, height: 300 }}
                  base={{ width: 400, ar: "FourByThree" }}
                />
              </MediaQuery>
            </div>
          </div>
        </div>
      </div>

      <EventListSkeleton />
    </>
  );
};

export default OrganizationEventList;
